/**
 * Search-engine facts about ONE listing: is it indexable, what is its canonical URL, what goes into
 * <title>, <meta name="description"> and the image alt texts.
 *
 * Single source of truth on the client. The server keeps a copy of the indexability predicate in
 * server/api-util/seoCatalog.js (sitemaps); a parity test there imports both, so a change here
 * that is not mirrored there fails the build instead of putting a noindex page into a sitemap.
 *
 * Why the text is built here and not with the existing `ListingPage.schemaTitle` key: that key is
 * hosted in the Sharetribe Console, which silently overrides the repo value. Every string below
 * uses NEW keys (ListingPage.seo*, ListingPage.photoAlt, ListingPage.breadcrumb*).
 *
 * The predicates accept a listing entity (`{ attributes: { publicData } }`, as the SDK returns it)
 * or its bare attributes (`{ publicData }`), so the server copy and its parity test can feed the
 * same fixtures to both.
 */
import { LOUNGE_MATCHER } from './airportLoungeConfig';
import { getPriceUnit } from './bookingDuration';
import { isFixdeskListing } from './fixdesk';
import { htmlToPlainText } from './sanitize';
import { safeExternalBookingUrl } from './urlHelpers';

// Google cuts titles at roughly 60 characters. The brand suffix is only worth having when the
// whole thing fits; a cut-off "| cowo…" helps nobody.
export const SEO_TITLE_MAX_LENGTH = 60;
// Snippet length on desktop; longer descriptions are cut by Google at an arbitrary point.
export const SEO_DESCRIPTION_MAX_LENGTH = 155;

const LOUNGE_LISTING_TYPE_REGEX = /^lounge$/i;
// "ending in" Schweiz — the importer writes `<Airport (IATA)>, <City>, Schweiz`, a space
// `<Street>, <PLZ> <Ort>, Schweiz`.
const SWISS_ADDRESS_REGEX = /(?:^|,)\s*(?:schweiz|switzerland|suisse|svizzera)\s*$/i;

const attributesOf = listing => listing?.attributes || listing || {};
const publicDataOf = listing => attributesOf(listing).publicData || {};

/**
 * Airport lounge: hosted category `lounge` (matched loosely, like everywhere else in the app) or
 * the `Lounge` listing type.
 *
 * @param {Object} listing listing entity or its attributes
 * @returns {boolean}
 */
export const isLoungeListing = listing => {
  const { categoryLevel1, listingType } = publicDataOf(listing);
  return (
    (typeof categoryLevel1 === 'string' && LOUNGE_MATCHER.test(categoryLevel1)) ||
    (typeof listingType === 'string' && LOUNGE_LISTING_TYPE_REGEX.test(listingType))
  );
};

/**
 * The partner booking URL of a listing that is booked off-site (LoungePair deeplink), or null.
 * Same allowlist check as the OrderPanel CTA, so structured data never names a URL the page
 * itself would refuse to link.
 *
 * @param {Object} listing listing entity or its attributes
 * @returns {string|null}
 */
export const externalBookingUrlOf = listing =>
  safeExternalBookingUrl(publicDataOf(listing).externalBookingUrl);

/**
 * A lounge whose booking happens on a partner site (LoungePair). cowork24 sells nothing on these
 * pages — which decides both their indexability (D3) and their Offer markup.
 *
 * @param {Object} listing listing entity or its attributes
 * @returns {boolean}
 */
export const isAffiliateLoungeListing = listing =>
  isLoungeListing(listing) && !!externalBookingUrlOf(listing);

/**
 * In Switzerland? `publicData.countryCode` when the listing has one (every imported lounge does),
 * otherwise the country at the end of `publicData.location.address`.
 *
 * @param {Object} listing listing entity or its attributes
 * @returns {boolean}
 */
export const isSwissListing = listing => {
  const { countryCode, location } = publicDataOf(listing);
  if (typeof countryCode === 'string' && countryCode.trim()) {
    return countryCode.trim().toUpperCase() === 'CH';
  }
  const address = location?.address;
  return typeof address === 'string' && SWISS_ADDRESS_REGEX.test(address);
};

/**
 * Decision D3 of the SEO plan: airport lounges outside Switzerland are templated affiliate pages
 * (~296 of them, the Search Console soft-404 pattern) and get `noindex, follow` plus no sitemap
 * entry. Everything else — spaces anywhere, the Swiss lounges — stays indexable.
 *
 * Listing state (closed, draft) is NOT part of this predicate; the page adds that itself.
 *
 * @param {Object} listing listing entity or its attributes
 * @returns {boolean}
 */
export const isIndexableListing = listing =>
  !(isLoungeListing(listing) && !isSwissListing(listing));

/**
 * Decision D1: the canonical listing URL is slugless. Every machine signal (canonical, og:url,
 * Product @id, offers.url, breadcrumbs, sitemaps) uses it; visible links keep their slug.
 *
 * @param {string|{uuid: string}} id listing id
 * @returns {string} e.g. '/l/6ab2cacc-…'
 */
export const listingCanonicalPath = id => `/l/${id?.uuid || id}`;

/**
 * @param {string} rootURL config.marketplaceRootURL
 * @param {string|{uuid: string}} id listing id
 * @returns {string} absolute canonical URL
 */
export const listingCanonicalUrl = (rootURL, id) =>
  `${(rootURL || '').replace(/\/+$/, '')}${listingCanonicalPath(id)}`;

const COUNTRY_CODES = {
  schweiz: 'CH',
  switzerland: 'CH',
  suisse: 'CH',
  svizzera: 'CH',
  liechtenstein: 'LI',
  deutschland: 'DE',
  germany: 'DE',
  österreich: 'AT',
  oesterreich: 'AT',
  austria: 'AT',
  frankreich: 'FR',
  france: 'FR',
  italien: 'IT',
  italy: 'IT',
  italia: 'IT',
};
const POSTAL_LOCALITY_REGEX = /^(\d{4,5})\s+(.+)$/;
const HAS_DIGIT_REGEX = /\d/;

/**
 * Split `publicData.location.address` ("Josefstrasse 214a, 8005 Zürich, Schweiz") into
 * PostalAddress fields. Parts that cannot be recognised are left out rather than guessed:
 * "Adelboden, Kanton Bern, Schweiz" gives a locality and a country, no street.
 *
 * @param {string} address
 * @returns {{streetAddress?: string, postalCode?: string, addressLocality?: string, addressCountry?: string}}
 */
export const parseListingAddress = address => {
  if (typeof address !== 'string' || !address.trim()) {
    return {};
  }
  const parts = address
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  if (parts.length < 2) {
    return {};
  }
  const countryName = parts[parts.length - 1];
  const addressCountry = COUNTRY_CODES[countryName.toLowerCase()];
  const head = parts.slice(0, -1);

  const postalIndex = head.findIndex(part => POSTAL_LOCALITY_REGEX.test(part));
  if (postalIndex >= 0) {
    const [, postalCode, addressLocality] = head[postalIndex].match(POSTAL_LOCALITY_REGEX);
    const streetAddress = postalIndex > 0 ? head.slice(0, postalIndex).join(', ') : undefined;
    return {
      ...(streetAddress ? { streetAddress } : {}),
      postalCode,
      addressLocality: addressLocality.trim(),
      ...(addressCountry ? { addressCountry } : {}),
    };
  }

  const addressLocality = head.find(part => !HAS_DIGIT_REGEX.test(part));
  return {
    ...(addressLocality ? { addressLocality } : {}),
    ...(addressCountry ? { addressCountry } : {}),
  };
};

// German names for the airports a German-speaking searcher types ("Lounge Flughafen Zürich").
// Everything else falls back to the importer's English airport name without "Airport".
const AIRPORT_LABELS_DE = {
  ZRH: 'Zürich',
  GVA: 'Genf',
  BSL: 'Basel-Mulhouse',
  BRN: 'Bern',
  LUG: 'Lugano',
  LHR: 'London-Heathrow',
  LGW: 'London-Gatwick',
  LTN: 'London-Luton',
  STN: 'London-Stansted',
  CDG: 'Paris-Charles de Gaulle',
  ORY: 'Paris-Orly',
  FCO: 'Rom-Fiumicino',
  MXP: 'Mailand-Malpensa',
  LIN: 'Mailand-Linate',
  BGY: 'Mailand-Bergamo',
  VCE: 'Venedig',
  NCE: 'Nizza',
  MUC: 'München',
  FRA: 'Frankfurt',
  DUS: 'Düsseldorf',
  BER: 'Berlin-Brandenburg',
  VIE: 'Wien',
  CPH: 'Kopenhagen',
  ATH: 'Athen',
  PRG: 'Prag',
  WAW: 'Warschau',
  LIS: 'Lissabon',
  BRU: 'Brüssel',
  IST: 'Istanbul',
  SAW: 'Istanbul-Sabiha Gökçen',
};
const CITY_NAMES_DE = {
  zurich: 'Zürich',
  geneva: 'Genf',
  munich: 'München',
  vienna: 'Wien',
  rome: 'Rom',
  milan: 'Mailand',
  venice: 'Venedig',
  nice: 'Nizza',
  copenhagen: 'Kopenhagen',
  athens: 'Athen',
  prague: 'Prag',
  warsaw: 'Warschau',
  lisbon: 'Lissabon',
  brussels: 'Brüssel',
  cologne: 'Köln',
  florence: 'Florenz',
  naples: 'Neapel',
};
const IATA_IN_TITLE_REGEX = /\(([A-Z]{3})\)/;
const AIRPORT_SUFFIX_REGEX = /\s+(?:international\s+)?airport\b.*$/i;

const isGerman = locale => typeof locale === 'string' && locale.toLowerCase().startsWith('de');

const loungeIata = listing => {
  const { iata } = publicDataOf(listing);
  if (typeof iata === 'string' && /^[A-Za-z]{3}$/.test(iata.trim())) {
    return iata.trim().toUpperCase();
  }
  const match = (attributesOf(listing).title || '').match(IATA_IN_TITLE_REGEX);
  return match ? match[1] : null;
};

// "Zurich Airport (ZRH), Zurich, Schweiz" → "Zurich"
const loungeCityPart = listing => {
  const address = publicDataOf(listing).location?.address;
  const parts = typeof address === 'string' ? address.split(',').map(p => p.trim()) : [];
  return parts.length >= 3 ? parts[parts.length - 2] : null;
};

/**
 * "Zürich", "London-Gatwick", "Heydar Aliyev" — the airport as a German-speaking searcher names it.
 *
 * @param {Object} listing lounge listing
 * @param {string} locale intl.locale
 * @returns {string|null}
 */
export const loungeAirportLabel = (listing, locale) => {
  const iata = loungeIata(listing);
  if (isGerman(locale) && iata && AIRPORT_LABELS_DE[iata]) {
    return AIRPORT_LABELS_DE[iata];
  }
  const { airportName } = publicDataOf(listing);
  if (typeof airportName === 'string' && airportName.trim()) {
    return airportName.replace(AIRPORT_SUFFIX_REGEX, '').trim() || airportName.trim();
  }
  return loungeCityPart(listing);
};

/**
 * The place a listing is in: the town after the postal code for a space ("Zürich", "Rapperswil
 * SG"), the city for a lounge ("Zürich", "London").
 *
 * @param {Object} listing listing entity or its attributes
 * @param {string} [locale] intl.locale — German city names for lounges when 'de'
 * @returns {string|null}
 */
export const listingLocality = (listing, locale = 'de') => {
  if (isLoungeListing(listing)) {
    const city = loungeCityPart(listing);
    if (!city) {
      return null;
    }
    const german = isGerman(locale) ? CITY_NAMES_DE[city.toLowerCase()] : null;
    return german || city;
  }
  return parseListingAddress(publicDataOf(listing).location?.address).addressLocality || null;
};

/**
 * The kind of space, as a select value of ListingPage.seoTypeLabel. Category first (it is the
 * room), then the day-pass booking model for desk-like spaces (it is what people search for).
 *
 * @param {Object} listing listing entity or its attributes
 * @returns {'lounge'|'meeting'|'virtualOffice'|'teamOffice'|'office'|'coliving'|'daypass'|'fixedDesk'|'desk'|'coworking'|'other'}
 */
export const listingTypeKey = listing => {
  if (isLoungeListing(listing)) {
    return 'lounge';
  }
  const { categoryLevel1, listingType } = publicDataOf(listing);
  const category = typeof categoryLevel1 === 'string' ? categoryLevel1.toLowerCase() : '';
  if (/meeting|konferenz|seminar/.test(category)) return 'meeting';
  if (/virtual/.test(category)) return 'virtualOffice';
  if (/team.?office/.test(category)) return 'teamOffice';
  if (/office|b(?:ü|ue|u)ro/.test(category)) return 'office';
  if (/coliving/.test(category)) return 'coliving';
  if (typeof listingType === 'string' && /tagespass/i.test(listingType)) return 'daypass';
  if (/fix(?:ed)?.?desk/.test(category)) return 'fixedDesk';
  if (/desk|open.?space/.test(category)) return 'desk';
  if (/community|cowork/.test(category)) return 'coworking';
  return 'other';
};

/**
 * @param {Object} listing listing entity
 * @param {Object} intl react-intl
 * @returns {string} e.g. "Meetingraum", "Coworking-Tagespass"
 */
export const listingTypeLabel = (listing, intl) =>
  intl.formatMessage({ id: 'ListingPage.seoTypeLabel' }, { type: listingTypeKey(listing) });

/**
 * The lowest price the listing really sells at: listing.price and, when price variations are on,
 * every variant. (On prod listing.price already is the cheapest variant; this does not rely on
 * it.) Never invents a price: null when there is none.
 *
 * @param {Object} listing listing entity or its attributes
 * @returns {{amount: number, currency: string}|null} amount in subunits
 */
export const lowestListingPrice = listing => {
  const { price } = attributesOf(listing);
  const { priceVariationsEnabled, priceVariants } = publicDataOf(listing);
  const currency = price?.currency;
  if (!currency) {
    return null;
  }
  const isPositive = amount => Number.isFinite(amount) && amount > 0;
  const amounts = [price.amount];
  if (priceVariationsEnabled && Array.isArray(priceVariants)) {
    priceVariants.forEach(variant => amounts.push(variant?.priceInSubunits));
  }
  const valid = amounts.filter(isPositive);
  return valid.length > 0 ? { amount: Math.min(...valid), currency } : null;
};

/**
 * What one unit of `amount` buys — only when it is exactly one unit (see getPriceUnit), so a
 * 2-day package is never advertised as a day rate.
 *
 * @param {Object} listing listing entity or its attributes
 * @param {number} [amount] the advertised amount in subunits
 * @returns {'hour'|'day'|'night'|'week'|'month'|'person'|null}
 */
export const listingPriceUnit = (listing, amount) => {
  const publicData = publicDataOf(listing);
  if (isLoungeListing(listing)) {
    // Lounge passes are priced per visitor — the imported descriptions say "pro Person" too.
    return 'person';
  }
  if (isFixdeskListing(publicData)) {
    // TEMPORARY fixdesk model (util/fixdesk.js): the purchase price buys one month.
    return 'month';
  }
  return getPriceUnit(publicData, amount) || null;
};

/**
 * "CHF 25", "CHF 12,50" — no grouping, so "CHF 5100" can't read as 5.1 in de.
 *
 * @param {Object} intl react-intl
 * @param {{amount: number, currency: string}} money amount in subunits
 * @returns {string}
 */
export const formatSeoPrice = (intl, money) => {
  const value = money.amount / 100;
  const digits = Number.isInteger(value) ? 0 : 2;
  const number = intl.formatNumber(value, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: false,
  });
  return `${money.currency} ${number}`;
};

// A title that already says what kind of space it is does not need " – Meetingraum" appended.
const TYPE_WORD_REGEX = /tages(?:pass|suite|b(?:ü|ue|u)ro|platz|ticket)|day.?pass|arbeitspl(?:a|ä|ae)tz|einzel(?:platz|arbeitsplatz)|fix(?:er|e)?\s*pl(?:a|ä|ae)tz|desk|b(?:ü|ue)ro|office|sitzungszimmer|meeting|konferenz|seminar|workshop|event.?space|co.?working|workspace|coliving|lounge/i;

const foldForMatch = text =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/ae/g, 'a')
    .replace(/oe/g, 'o')
    .replace(/ue/g, 'u');

// "Rapperswil SG" is in a title that says "Rapperswil"; "Leuk Stadt" in one that says "Leuk".
const CANTON_OR_DISTRICT_SUFFIX_REGEX = /\s+(?:[A-Z]{2}|Stadt)$/;

const textMentionsPlace = (text, place) => {
  const haystack = foldForMatch(text);
  return [place, place.replace(CANTON_OR_DISTRICT_SUFFIX_REGEX, '')]
    .map(variant => variant.trim())
    .filter(Boolean)
    .some(variant => haystack.includes(foldForMatch(variant)));
};

// "Zurich Airport (ZRH) — Primeclass Lounge" → "Primeclass Lounge"
const loungeNameFromTitle = title => {
  const segments = title.split(/\s+[—–]\s+/);
  return (segments[segments.length - 1] || title).trim();
};

// "E Gates (Dock E) — Non-Schengen" → "E Gates (Dock E)"; "Main Transit Area, Mezzanine" →
// "Main Transit Area". It tells the terminals apart — three "Plaza Premium Lounge" at Heathrow.
const shortTerminal = building =>
  typeof building === 'string' ? building.split(/\s+[—–-]\s+|,/)[0].trim() : '';

const loungeBaseTitle = (listing, intl) => {
  const title = (attributesOf(listing).title || '').trim();
  const iata = loungeIata(listing);
  const airport = loungeAirportLabel(listing, intl.locale);
  if (!iata || !airport) {
    return title;
  }
  const name = loungeNameFromTitle(title);
  return intl.formatMessage(
    { id: 'ListingPage.seoTitleLounge' },
    { name, airport, iata, nameHasLounge: /lounge/i.test(name) ? 'yes' : 'no' }
  );
};

// Title + " in <Ort>" when the title does not name its place yet. Shared by <title> and alt text.
const titleWithLocality = (listing, intl) => {
  const title = (attributesOf(listing).title || '').trim();
  const locality = listingLocality(listing, intl.locale);
  return locality && title && !textMentionsPlace(title, locality)
    ? intl.formatMessage({ id: 'ListingPage.seoTitleIn' }, { title, locality })
    : title;
};

/**
 * The <title> (and og:title) of a listing page. The H1 stays the provider's own title.
 *
 * - space: title + " in <Ort>" (if the title does not name it) + " – <Typ>" (if the title has
 *   no type word); "Desk" in St. Gallen → "Desk in St. Gallen | cowork24"
 * - lounge: "<Lounge> – Lounge Flughafen <Airport> (<IATA>), <Terminal>"
 * - " | <marketplace>" only when the whole title stays within 60 characters.
 *
 * @param {Object} params
 * @param {Object} params.listing listing entity
 * @param {Object} params.intl react-intl
 * @param {string} params.marketplaceName e.g. 'cowork24'
 * @returns {string}
 */
export const listingSeoTitle = ({ listing, intl, marketplaceName }) => {
  const rawTitle = (attributesOf(listing).title || '').trim();
  let title;
  if (isLoungeListing(listing)) {
    const terminal = shortTerminal(publicDataOf(listing).location?.building);
    const base = loungeBaseTitle(listing, intl);
    title = terminal ? `${base}, ${terminal}` : base;
  } else {
    title = titleWithLocality(listing, intl);
    if (rawTitle && !TYPE_WORD_REGEX.test(rawTitle)) {
      title = `${title} – ${listingTypeLabel(listing, intl)}`;
    }
  }

  if (!title) {
    return marketplaceName || '';
  }
  const branded = marketplaceName ? `${title} | ${marketplaceName}` : title;
  return branded.length <= SEO_TITLE_MAX_LENGTH ? branded : title;
};

/**
 * Cut text at a word boundary so that it, including the trailing "…", is at most `max` long.
 *
 * @param {string} text
 * @param {number} [max]
 * @returns {string}
 */
export const truncateAtWordBoundary = (text, max = SEO_DESCRIPTION_MAX_LENGTH) => {
  if (typeof text !== 'string') {
    return '';
  }
  if (text.length <= max) {
    return text;
  }
  const room = max - 1;
  let cut = text.slice(0, room);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > room / 2) {
    cut = cut.slice(0, lastSpace);
  }
  // Never end on half of a surrogate pair (emoji in a description).
  if (/[\ud800-\udbff]$/.test(cut)) {
    cut = cut.slice(0, -1);
  }
  return `${cut.replace(/[\s,;:.·(\-–—]+$/, '')}…`;
};

/**
 * The meta description: a typed lead with the facts a searcher compares — "Meetingraum in
 * Winterthur · ab CHF 4 pro Stunde." — followed by the description as plain text, cut to 155
 * characters at a word boundary. The price is the lowest real one (listing price or variant);
 * the unit only when that price buys exactly one unit.
 *
 * @param {Object} params
 * @param {Object} params.listing listing entity
 * @param {Object} params.intl react-intl
 * @returns {string}
 */
export const listingSeoDescription = ({ listing, intl }) => {
  const lowest = lowestListingPrice(listing);
  const unit = lowest ? listingPriceUnit(listing, lowest.amount) : null;
  const lead = intl.formatMessage(
    { id: 'ListingPage.seoDescriptionLead' },
    {
      type: listingTypeLabel(listing, intl),
      locality: listingLocality(listing, intl.locale) || 'none',
      price: lowest ? formatSeoPrice(intl, lowest) : 'none',
      unit: unit || 'none',
    }
  );
  const body = htmlToPlainText(attributesOf(listing).description);
  return truncateAtWordBoundary(body ? `${lead} ${body}` : lead);
};

/**
 * The listing as image alt text names it: "Sitzungszimmer gross in Rapperswil SG",
 * "Primeclass Lounge – Flughafen Zürich (ZRH)".
 *
 * @param {Object} listing listing entity
 * @param {Object} intl react-intl
 * @returns {string}
 */
export const listingPhotoAltTitle = (listing, intl) =>
  isLoungeListing(listing) ? loungeBaseTitle(listing, intl) : titleWithLocality(listing, intl);

/**
 * Alt text of the n-th listing photo. With a title: "<title> – Foto 2 von 6" (new key
 * ListingPage.photoAlt). Without one it falls back to a plain "Foto 2 von 6" so nothing renders an
 * empty alt.
 *
 * @param {Object} intl react-intl
 * @param {Object} params
 * @param {string} [params.title] from listingPhotoAltTitle
 * @param {number} params.index zero-based
 * @param {number} params.count
 * @param {string} [params.fallbackId] message id used when there is no title
 * @returns {string}
 */
export const listingPhotoAlt = (
  intl,
  { title, index, count, fallbackId = 'ListingImageGallery.photoAltUntitled' }
) =>
  title
    ? intl.formatMessage({ id: 'ListingPage.photoAlt' }, { title, index: index + 1, count })
    : intl.formatMessage({ id: fallbackId }, { index: index + 1, count });
